import { useCallback, useEffect, useMemo, useState } from 'react'
import { Input, Modal, Radio, Typography } from '@musinsa/pbo-core'
import { useGetExportCombo } from '@/services/excel/useExcelQuries'
import type { StockLedgerDownloadReason } from '@/services/inventory/stockLedger/payload'

/** 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일. */
const REASON_DETAIL_MIN_LENGTH = 5

interface Props {
  open: boolean
  // 사유 타입은 페이로드 계약(payload.ts)이 원본이다 — 모달이 따로 타입을 노출하지 않는다.
  onConfirm: (reason: StockLedgerDownloadReason) => void
  onCancel: () => void
}

/** 상세사유 입력 id — 라벨(htmlFor)과 묶어 스크린리더가 함께 읽게 한다. */
const REASON_DETAIL_ID = 'excel-reason-detail'

/**
 * 필수 입력 표시 — 사유코드·상세사유 모두 필수다.
 * `htmlFor` 가 오면 실제 입력에 묶이는 label 로, 없으면(라디오 그룹처럼 자체 라벨을 갖는 경우) 표시용 텍스트로 그린다.
 */
function RequiredLabel({ text, htmlFor }: Readonly<{ text: string; htmlFor?: string }>) {
  return (
    <Typography.Text>
      {htmlFor ? <label htmlFor={htmlFor}>{text}</label> : text} <span style={{ color: 'var(--Colors-Brand-Error-colorError, #ff4d4f)' }}>*</span>
    </Typography.Text>
  )
}

/**
 * ST10 엑셀 다운로드 사유 모달. (1.0 RemarkPop 동치 — 핸드오프 스키마 `m-download-reason`)
 *
 * 사유코드는 1.0 공용 콤보(`DOWNLOAD_CD`)에서 받아 **한 줄로** 노출하고, 사유코드·상세사유 모두 필수다.
 * 확정 사유는 엑셀 요청 페이로드에 실어 보낸다 — 단 **BE 2.0 은 현재 사유 파라미터를 받지 않는다**
 * (LOGISPRD-9594 LLD §7 미이관 확정, 미등록 키는 무시). 감사로그 remark 반영이 필요해지면 BE 확장이 필요하다.
 *
 * 공용 `ExcelDownloadModal`(파일 비밀번호 필수)은 쓰지 않는다 — 파일 암호화는 GI/RGR 전용이고 ST03 은
 * PII 없는 재고 집계라 1.0 에서도 평문이다. pbo-core `logistics/ExcelDownloadReasonModal` 도 쓰지 않는다 —
 * `onConfirm` 이 무인자라 사유 값을 꺼낼 수 없다(ST02 선례와 동일한 판단).
 */
export function ExcelDownloadReasonModal({ open, onConfirm, onCancel }: Readonly<Props>) {
  const { data: combo, isLoading: isComboLoading } = useGetExportCombo({ errorToast: true })
  // 콤보는 코드/명 배열이 짝을 이룬다. 명(COMBO_OPT_NM)이 비면 코드로 대체한다(센터명 콤보와 동일 규약).
  const options = useMemo(
    () =>
      (combo?.DOWNLOAD_CD?.COMBO_OPT_CD ?? []).map((code, idx) => ({
        value: code,
        label: combo?.DOWNLOAD_CD?.COMBO_OPT_NM?.[idx] || code,
      })),
    [combo],
  )

  const [reasonCode, setReasonCode] = useState('')
  const [reasonDetail, setReasonDetail] = useState('')

  // 열 때마다 초기화 — 이전 다운로드의 사유가 남지 않게 한다.
  useEffect(() => {
    if (open) {
      setReasonCode('')
      setReasonDetail('')
    }
  }, [open])

  const isValid = Boolean(reasonCode) && reasonDetail.trim().length >= REASON_DETAIL_MIN_LENGTH

  const handleOk = useCallback(() => {
    if (!isValid) return
    onConfirm({ reasonCode, reasonDetail: reasonDetail.trim() })
  }, [isValid, reasonCode, reasonDetail, onConfirm])

  return (
    <Modal open={open} title="다운로드 사유" width={560} okText="확인" cancelText="취소" okDisabled={!isValid} onOk={handleOk} onCancel={onCancel}>
      <RequiredLabel text="사유코드" />
      {/* 사유코드 옵션은 줄바꿈 없이 한 줄로 노출한다. 콤보를 받아오는 동안은 빈 라디오 대신 로딩을 알린다. */}
      <div style={{ marginTop: 8, whiteSpace: 'nowrap' }}>
        {isComboLoading ? (
          <Typography.Text type="secondary">사유코드를 불러오는 중입니다…</Typography.Text>
        ) : (
          <Radio.Group options={options} value={reasonCode} onChange={(e) => setReasonCode(String(e.target.value))} />
        )}
      </div>
      <div style={{ marginTop: 16 }}>
        <RequiredLabel text="상세사유" htmlFor={REASON_DETAIL_ID} />
        <Input.TextArea
          id={REASON_DETAIL_ID}
          rows={3}
          placeholder="사유를 입력하세요"
          value={reasonDetail}
          onChange={(e) => setReasonDetail(e.target.value)}
          style={{ marginTop: 8 }}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {`${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해주세요.`}
        </Typography.Text>
      </div>
    </Modal>
  )
}
